import { BitkitFilter } from '@bitrise/bitkit-v2';
import { DateTime } from 'luxon';
import { useState } from 'react';

import BuildListFilters from '@/pages/App/AppDetails/types/BuildListFilters';

import useAppBranchesForFilter from '../../hooks/useAppBranchesForFilter';
import useAppWorkflowsForFilter from '../../hooks/useAppWorkflowsForFilter';
import useBuildFilterState, { emptyBuildFilters } from '../hooks/useBuildFilterState';

type BuildFiltersProps = {
  appSlug: string;
  setFilters(filters: BuildListFilters): void;
};

const statusItems = [
  { label: 'Running', value: 'running' },
  { label: 'Successful', value: 'succeeded' },
  { label: 'Failed', value: 'failed' },
  { label: 'Aborted', value: 'aborted' },
  { label: 'Aborted With Success', value: 'succeeded_with_abort' },
  { label: 'On Hold', value: 'on_hold' },
];

const triggerItems = [
  { label: 'Pushes', value: 'push' },
  { label: 'Pull Requests', value: 'pull-request' },
  { label: 'Tags', value: 'tag' },
];

const artifactItems = [
  { label: 'iOS IPA', value: 'ios-ipa' },
  { label: 'Android APK/AAB', value: 'android-apk' },
];

const SEARCH_PLACEHOLDER = 'Type at least 3 characters';

// The API searches from 3 characters; shorter input lists the first options.
const searchKeyword = (text: string) => (text.length >= 3 ? text : '');

const BuildFilters = ({ appSlug, setFilters: updateFilters }: BuildFiltersProps) => {
  const [branchSearch, setBranchSearch] = useState('');
  const [workflowSearch, setWorkflowSearch] = useState('');
  const [minDate] = useState(() => DateTime.now().minus({ days: 200 }).startOf('day').toJSDate());

  const { data: workflowsAndPipelines, isLoading: isWorkflowsLoading } = useAppWorkflowsForFilter({
    appSlug,
    keyword: searchKeyword(workflowSearch),
    withPipelineWorkflows: false,
  });
  const { data: branches, isLoading: isBranchesLoading } = useAppBranchesForFilter({
    appSlug,
    keyword: searchKeyword(branchSearch),
  });

  const { values, applyFilters } = useBuildFilterState(appSlug, updateFilters);

  // Pipelines and workflows can share a name. The chip shows the label without its group, so a shared
  // name keeps its suffix.
  const pipelines = workflowsAndPipelines?.pipelines ?? [];
  const workflows = workflowsAndPipelines?.workflows ?? [];
  const workflowItems = [
    ...pipelines.map((name) => ({
      group: 'Pipelines',
      label: workflows.includes(name) ? `${name} (pipeline)` : name,
      value: `pipeline-${name}`,
    })),
    ...workflows.map((name) => ({
      group: 'Workflows',
      label: pipelines.includes(name) ? `${name} (workflow)` : name,
      value: `workflow-${name}`,
    })),
  ];

  return (
    <BitkitFilter
      data-testid="build-filters"
      onClear={() => applyFilters(emptyBuildFilters)}
      paddingBlockStart={{ tablet: '12' }}
    >
      {/* After the switches, before the selects, as the filters have always been ordered. */}
      <BitkitFilter.DateRange
        dayTooltip={(date) => {
          if (date < minDate) return 'Artifacts can be listed for up to 200 days.';
          if (date > DateTime.now().endOf('day').toJSDate()) return 'Can not select a date in the future.';
          return undefined;
        }}
        min={minDate}
        onValueChange={(dates) => applyFilters({ dates })}
        value={values.dates}
      />
      <BitkitFilter.Select
        items={(branches ?? []).map((branch) => ({ label: branch, value: branch }))}
        label="Branch"
        loading={isBranchesLoading}
        onSearchChange={setBranchSearch}
        onValueChange={(branch) => applyFilters({ branch })}
        pluralLabel="Branches"
        searchPlaceholder={SEARCH_PLACEHOLDER}
        value={values.branch}
      />
      <BitkitFilter.Select
        items={workflowItems}
        label="Workflow/Pipeline"
        loading={isWorkflowsLoading}
        onSearchChange={setWorkflowSearch}
        onValueChange={(workflow) => applyFilters({ workflow })}
        pluralLabel="Workflows/pipelines"
        searchPlaceholder={SEARCH_PLACEHOLDER}
        value={values.workflow}
      />
      <BitkitFilter.Select
        items={statusItems}
        label="Status"
        onValueChange={(status) => applyFilters({ status })}
        pluralLabel="Statuses"
        value={values.status}
      />
      <BitkitFilter.Select
        items={triggerItems}
        label="Trigger"
        onValueChange={(triggerEventType) => applyFilters({ triggerEventType })}
        pluralLabel="Triggers"
        value={values.triggerEventType}
      />
      <BitkitFilter.Select
        items={artifactItems}
        label="Artifact"
        onValueChange={(artifactType) => applyFilters({ artifactType })}
        pluralLabel="Artifacts"
        value={values.artifactType}
      />
      <BitkitFilter.Search
        onValueChange={(search) => applyFilters({ search })}
        tooltip="Build number or commit message"
        value={values.search}
      />
    </BitkitFilter>
  );
};

export default BuildFilters;
